import { AxiosError, isAxiosError } from 'axios';

interface IApiErrorResponseData {
  message?: string;
  error?: string;
  data?: { message?: string; error?: string };
}

const firstString = (...values: Array<string | undefined>): string | undefined =>
  values.find((v): v is string => typeof v === 'string' && v.trim().length > 0);

/**
 * Builds a domain's catch-all API error: the class a service puts LAST in its
 * error chain, after the specific typed errors (rate limit, invalid
 * credentials, …), so the user sees whatever copy the BE wrote instead of the
 * generic axios "Request failed with status code …" string.
 *
 * Each call returns its own base class, so `instanceof` tells the domains apart
 * and `error.name` (what Datadog reports) is the domain's class name. A domain
 * extends it under that name, which gives it a value and a type in one export.
 *
 * `TField` names the form field a rejection can be tagged with, for a form that
 * paints the field's error border without pattern-matching BE copy. A domain
 * that tags none leaves it out.
 *
 * @example
 * export class SignInApiError extends createApiError('SignInApiError') {}
 */
export const createApiError = <TField extends string = never>(name: string) => {
  class ApiError extends Error {
    declare readonly field?: TField;

    constructor(message: string, field?: TField) {
      super(message);
      this.name = name;
      if (field !== undefined) {
        this.field = field;
      }
    }

    /**
     * The BE's own copy as an instance of this class, read from `message`,
     * `error`, then the gateway `data` envelope; null for a non-axios error or
     * a response with no usable copy, so the caller rethrows the original.
     * Built with `new this`, so a subclass gets an instance of itself.
     */
    static fromAxiosError<T extends ApiError>(
      this: new (message: string, field?: TField) => T,
      error: unknown,
      field?: TField,
    ): T | null {
      if (!isAxiosError(error)) {
        return null;
      }

      const axiosError = error as AxiosError<IApiErrorResponseData>;
      const data = axiosError.response?.data;
      const message = firstString(
        data?.message,
        data?.error,
        data?.data?.message,
        data?.data?.error,
      );

      return message ? new this(message, field) : null;
    }
  }

  return ApiError;
};
